// ONE PARTNER'S POSITION, AS FIVE LINES OF PLAIN ARITHMETIC.
//
// This is the whole screen's answer, and its shape is the point. There was a table here
// before — seven columns per income unit, share and cash profit and entitlement and
// contribution, a fold per partner and a foot that proved it all added up. Every figure
// in it was right and nobody could read it. What a reader actually asks is "what came in
// to me, what went out of me, and how does that compare with what I'm due", so that is
// what this prints, in that order, with every intermediate visible:
//
//     נגבה אצלי                 collected
//   − שילמתי מהכיס              expensesPaid
//   ─────────────────
//   = יש אצלי ביד               cash        (the server's own `cash` field)
//     מגיע לי לפי החלק ברווח    entitled
//   ─────────────────
//   = הפער                      netOwed = entitled − cash
//
// NOT A TABLE, and not because a table would look worse — because a table invites the
// eye across the row to compare two partners, and the comparison that matters here is
// DOWN one partner's own column. The two statements sit side by side and each reads as
// a sentence about one person.
//
// THE CLIENT ADDS NOTHING. Every one of the five figures is a field the server already
// computed and already asserted (settlementCalc's zero-sum check runs before this ever
// arrives). `cash` is NOT re-derived here as collected − expensesPaid, even though it
// would come out the same: the rule is that the browser never re-implements a server
// formula, and a subtraction is exactly how that rule starts getting broken.
import he from '../../i18n/he';
import Money from '../../components/Money';

const t = he.settlements;

// One line of the statement. `op` is the arithmetic sign shown in the margin — '−' on
// the line being subtracted, nothing elsewhere — so the reader can see it is a sum
// rather than a list of four unrelated numbers.
const Line = ({ label, hint, value, op, strong, tone }) => (
  <div className={`settle-line${strong ? ' strong' : ''}`}>
    <span className="settle-line-label">
      {op && <span className="settle-line-op">{op}</span>}
      {label}
      {hint && <span className="muted settle-line-hint"> ({hint})</span>}
    </span>
    <span className="settle-line-value">
      {tone ? <Money value={value} className={tone} /> : <Money value={value} />}
    </span>
  </div>
);

// The last line, phrased as credit/debit so a negative figure never reads backwards.
// Same wording the screen has always used — only its position changed.
const GapLine = ({ value }) => {
  const settled = Math.abs(value) < 0.005;
  return (
    <div className="settle-line strong settle-line-gap">
      <span className="settle-line-label">{t.statement.gap}</span>
      <span className="settle-line-value">
        {settled ? (
          <span className="muted">{t.settled}</span>
        ) : (
          <>
            <span className="muted settle-line-direction">
              {value > 0 ? t.credit : t.debit}
            </span>{' '}
            <Money value={Math.abs(value)} className={value > 0 ? 'pos' : 'neg'} />
          </>
        )}
      </span>
    </div>
  );
};

export default function PartnerStatement({ partner }) {
  return (
    <div className="settle-statement">
      <h3 className="settle-statement-name">{partner.name}</h3>
      <Line label={t.statement.collected} value={partner.collected} />
      <Line
        label={t.statement.paidFromPocket}
        hint={t.statement.paidFromPocketHint}
        value={partner.expensesPaid}
        op="−"
      />
      <Line label={t.statement.inHand} value={partner.cash} strong />
      <Line label={t.statement.entitled} value={partner.entitled} />
      <GapLine value={partner.netOwed} />
    </div>
  );
}
